import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useI18n } from "../lib/i18n";
import { useAuth, API } from "../lib/auth";
import StatusBadge, { STATUS_LIST } from "../components/StatusBadge";
import EmployerReports from "../components/EmployerReports";
import EmployerCareerEditor from "../components/EmployerCareerEditor";
import CandidateDatabase from "../components/CandidateDatabase";
import ApplicationInsightsDialog from "../components/ApplicationInsightsDialog";
import QualificationBadge, { MatchScoreBadge, qualificationFromScore } from "../components/QualificationBadge";
import { Briefcase, Users, CreditCard, Sparkles, ArrowUpDown, FileDown, Plus, Eye, BarChart3, Building2, Database } from "lucide-react";

export default function EmployerDashboard() {
  const { t } = useI18n();
  const { user, authHeader, token } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [apps, setApps] = useState([]);
  const [sort, setSort] = useState({ key: "created_at", order: "desc" });
  const [insightAppId, setInsightAppId] = useState(null);
  const [insightOpen, setInsightOpen] = useState(false);
  const [typeFilter, setTypeFilter] = useState("all"); // all|local|overseas_eligible|international|unknown
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [locStats, setLocStats] = useState({ counts: { local: 0, overseas_eligible: 0, international: 0, unknown: 0 }, total: 0, percentages: {} });

  const load = async () => {
    try {
      const [s, j, a, ls] = await Promise.all([
        axios.get(`${API}/employer/dashboard`, { headers: authHeader }),
        axios.get(`${API}/employer/jobs`, { headers: authHeader }),
        axios.get(`${API}/employer/applications`, { headers: authHeader, params: typeFilter !== "all" ? { applicant_type: typeFilter } : {} }),
        axios.get(`${API}/employer/applications/stats`, { headers: authHeader }),
      ]);
      setStats(s.data.stats); setJobs(j.data); setApps(a.data);
      setLocStats(ls.data || { counts: {}, total: 0, percentages: {} });
    } catch (e) { /* ignore */ }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [typeFilter]);

  const sorted = useMemo(() => {
    const arr = [...apps];
    arr.sort((a, b) => {
      const av = a[sort.key], bv = b[sort.key];
      if (av === bv) return 0;
      return (av > bv ? 1 : -1) * (sort.order === "asc" ? 1 : -1);
    });
    return arr;
  }, [apps, sort]);

  const toggleSort = (key) => setSort(s => ({ key, order: s.key === key && s.order === "desc" ? "asc" : "desc" }));

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/employer/applications/${id}/status`, { status }, { headers: authHeader });
      setApps(prev => prev.map(a => a.id === id ? { ...a, status } : a));
      toast.success("Statut mis à jour");
    } catch (e) { toast.error("Update failed"); }
  };

  const bulkDo = async (action, extra = {}) => {
    if (selectedIds.size === 0) return;
    if (action === "reject" && !window.confirm(`You are about to reject ${selectedIds.size} applications. Continue?`)) return;
    try {
      const r = await axios.post(`${API}/employer/applications/bulk`, { ids: Array.from(selectedIds), action, ...extra }, { headers: authHeader });
      toast.success(`${r.data.modified} applications updated`);
      setSelectedIds(new Set());
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk failed"); }
  };

  const bulkSelectAllFiltered = async () => {
    // Select every application currently matching the active filters (not just this page)
    setSelectedIds(new Set(sorted.map(a => a.id)));
  };

  const classifyOne = async (id, applicant_type) => {
    try {
      await axios.patch(`${API}/employer/applications/${id}/classify`, { applicant_type }, { headers: authHeader });
      setApps(prev => prev.map(a => a.id === id ? { ...a, applicant_type, location_verified: true } : a));
      toast.success("Classification updated");
      // Refresh stats
      const ls = await axios.get(`${API}/employer/applications/stats`, { headers: authHeader });
      setLocStats(ls.data);
    } catch (e) { toast.error("Update failed"); }
  };

  const viewCV = async (fileId) => {
    try {
      const res = await axios.get(`${API}/files/${fileId}/download`, { headers: authHeader, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
    } catch (e) { toast.error("CV not available"); }
  };

  return (
    <div className="bg-slate-50 min-h-[calc(100vh-4rem)]" data-testid="employer-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-end justify-between mb-8">
          <div>
            <span className="text-xs uppercase tracking-[0.25em] text-[#1A8FE3] font-semibold">{t("employer.dashboard")}</span>
            <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 mt-1">{user?.company_name || user?.name}</h1>
          </div>
          <Button onClick={() => navigate("/employer/post")} data-testid="employer-post-job" className="bg-[#1A8FE3] hover:bg-[#157AC0] text-white rounded-full h-11 px-5">
            <Plus className="h-4 w-4 mr-2" />{t("employer.post")}
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard icon={Users} label={t("employer.stats.totalApps")} value={stats?.total_applications ?? "—"} accent="text-sky-600" />
          <StatCard icon={Briefcase} label={t("employer.stats.activeJobs")} value={stats?.active_jobs ?? "—"} accent="text-emerald-600" />
          <StatCard icon={CreditCard} label={t("employer.stats.credits")} value={stats?.credits ?? "—"} accent="text-amber-600" />
          <StatCard icon={Sparkles} label={t("employer.stats.plan")} value={(stats?.plan || "").toUpperCase()} accent="text-indigo-600" />
        </div>

        <Tabs defaultValue="apps">
          <TabsList className="bg-white border border-slate-200 flex-wrap h-auto">
            <TabsTrigger value="apps" data-testid="emp-tab-apps">{t("employer.applications")}</TabsTrigger>
            <TabsTrigger value="jobs" data-testid="emp-tab-jobs">My Openings</TabsTrigger>
            <TabsTrigger value="candidates" data-testid="emp-tab-candidates"><Database className="h-4 w-4 mr-1" />Candidate DB</TabsTrigger>
            <TabsTrigger value="reports" data-testid="emp-tab-reports"><BarChart3 className="h-4 w-4 mr-1" />{t("employer.reports")}</TabsTrigger>
            <TabsTrigger value="career" data-testid="emp-tab-career"><Building2 className="h-4 w-4 mr-1" />{t("employer.career")}</TabsTrigger>
          </TabsList>

          <TabsContent value="apps" className="mt-6">
            {/* Quick-filter chips with live counts */}
            <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="apps-quick-filters">
              {[
                { v: "all", l: "All", c: locStats.total },
                { v: "local", l: "Local", c: locStats.counts?.local || 0 },
                { v: "overseas_eligible", l: "Overseas Eligible", c: locStats.counts?.overseas_eligible || 0 },
                { v: "international", l: "International", c: locStats.counts?.international || 0 },
                { v: "unknown", l: "Unknown", c: locStats.counts?.unknown || 0 },
              ].map(f => {
                const active = typeFilter === f.v;
                return (
                  <button
                    key={f.v}
                    type="button"
                    onClick={() => { setTypeFilter(f.v); setSelectedIds(new Set()); }}
                    data-testid={`apps-filter-${f.v}`}
                    className={`text-xs rounded-full px-3 py-1.5 border transition ${active ? "bg-[#1A8FE3] text-white border-[#1A8FE3]" : "bg-white text-slate-700 border-slate-200 hover:border-[#1A8FE3] hover:text-[#1A8FE3]"}`}
                  >
                    {f.l} <span className={`ml-1 text-[10px] font-medium ${active ? "text-white/80" : "text-slate-400"}`}>{f.c}</span>
                  </button>
                );
              })}
            </div>

            {/* Location analytics widget (per current filter scope) */}
            {locStats.total > 0 && (
              <Card className="p-4 mb-4" data-testid="apps-location-analytics">
                <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Applicant Location · Total {locStats.total}</div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  {["local","overseas_eligible","international","unknown"].map(k => (
                    <div key={k} className="rounded-lg bg-slate-50 border border-slate-100 p-3">
                      <div className="text-[10px] uppercase tracking-wider text-slate-500 capitalize">{k.replace("_"," ")}</div>
                      <div className="mt-1 text-lg font-bold text-slate-900">{locStats.counts?.[k] || 0} <span className="text-xs font-normal text-slate-500">— {locStats.percentages?.[k] ?? 0}%</span></div>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Bulk-action sticky bar */}
            {selectedIds.size > 0 && (
              <div className="mb-3 flex flex-wrap items-center gap-2 rounded-xl border border-[#1A8FE3]/30 bg-[#1A8FE3]/5 px-3 py-2" data-testid="bulk-actions-bar">
                <span className="text-sm font-medium text-slate-900">{selectedIds.size} selected</span>
                <span className="text-slate-300">|</span>
                <button onClick={() => bulkDo("shortlist")} data-testid="bulk-shortlist" className="text-xs px-3 py-1 rounded-full border border-slate-200 bg-white hover:border-emerald-500 hover:text-emerald-600">Shortlist</button>
                <button onClick={() => bulkDo("reject")} data-testid="bulk-reject" className="text-xs px-3 py-1 rounded-full border border-rose-200 bg-white text-rose-600 hover:bg-rose-50">Reject</button>
                <button onClick={() => bulkDo("mark_local")} data-testid="bulk-mark-local" className="text-xs px-3 py-1 rounded-full border border-slate-200 bg-white hover:border-[#1A8FE3]">Mark Local</button>
                <button onClick={() => bulkDo("mark_overseas_eligible")} data-testid="bulk-mark-eligible" className="text-xs px-3 py-1 rounded-full border border-slate-200 bg-white hover:border-[#1A8FE3]">Mark Overseas Eligible</button>
                <button onClick={() => bulkDo("mark_international")} data-testid="bulk-mark-intl" className="text-xs px-3 py-1 rounded-full border border-slate-200 bg-white hover:border-[#1A8FE3]">Mark International</button>
                <button onClick={() => bulkDo("mark_needs_review")} data-testid="bulk-mark-review" className="text-xs px-3 py-1 rounded-full border border-slate-200 bg-white hover:border-amber-500">Needs Review</button>
                <span className="flex-1" />
                <button onClick={() => setSelectedIds(new Set())} className="text-xs text-slate-500 hover:text-slate-800">Clear</button>
              </div>
            )}

            <Card className="overflow-hidden">
              {sorted.length === 0 ? (
                <div className="text-center py-16 text-slate-500">{t("employer.noApps")}</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-slate-700 uppercase text-xs tracking-wider">
                      <tr>
                        <th className="px-3 py-3 w-8">
                          <input
                            type="checkbox"
                            data-testid="apps-select-all"
                            checked={sorted.length > 0 && sorted.every(a => selectedIds.has(a.id))}
                            onChange={(e) => setSelectedIds(e.target.checked ? new Set(sorted.map(a => a.id)) : new Set())}
                            className="h-4 w-4 accent-[#1A8FE3]"
                          />
                        </th>
                        <th className="px-6 py-3 text-left">
                          <button type="button" data-testid="sort-candidate" onClick={() => toggleSort("candidate_name")} className="inline-flex items-center gap-1 hover:text-[#1A8FE3]">{t("employer.candidate")}<ArrowUpDown className="h-3 w-3" /></button>
                        </th>
                        <th className="px-4 py-3 text-left">Location</th>
                        <th className="px-6 py-3 text-left">
                          <button type="button" data-testid="sort-job" onClick={() => toggleSort("job_title")} className="inline-flex items-center gap-1 hover:text-[#1A8FE3]">{t("employer.job")}<ArrowUpDown className="h-3 w-3" /></button>
                        </th>
                        <th className="px-6 py-3 text-left">
                          <button type="button" data-testid="sort-date" onClick={() => toggleSort("created_at")} className="inline-flex items-center gap-1 hover:text-[#1A8FE3]">{t("employer.date")}<ArrowUpDown className="h-3 w-3" /></button>
                        </th>
                        <th className="px-6 py-3 text-left">{t("employer.contact")}</th>
                        <th className="px-6 py-3 text-left">
                          <button type="button" data-testid="sort-match" onClick={() => toggleSort("match_score")} className="inline-flex items-center gap-1 hover:text-[#1A8FE3]">Match<ArrowUpDown className="h-3 w-3" /></button>
                        </th>
                        <th className="px-6 py-3 text-left">Qualification</th>
                        <th className="px-6 py-3 text-left">
                          <button type="button" data-testid="sort-status" onClick={() => toggleSort("status")} className="inline-flex items-center gap-1 hover:text-[#1A8FE3]">{t("employer.status")}<ArrowUpDown className="h-3 w-3" /></button>
                        </th>
                        <th className="px-6 py-3 text-left">{t("employer.actions")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sorted.map(a => (
                        <tr key={a.id} data-testid={`app-row-${a.id}`} className="border-t border-slate-100 hover:bg-slate-50">
                          <td className="px-3 py-4">
                            <input
                              type="checkbox"
                              data-testid={`app-check-${a.id}`}
                              checked={selectedIds.has(a.id)}
                              onChange={(e) => setSelectedIds(prev => {
                                const next = new Set(prev);
                                if (e.target.checked) next.add(a.id); else next.delete(a.id);
                                return next;
                              })}
                              className="h-4 w-4 accent-[#1A8FE3]"
                            />
                          </td>
                          <td className="px-6 py-4 font-medium text-slate-900">
                            <button
                              type="button"
                              data-testid={`app-candidate-${a.id}`}
                              onClick={() => { setInsightAppId(a.id); setInsightOpen(true); }}
                              className="inline-flex items-center gap-1 text-left text-[#1A8FE3] hover:text-[#157AC0] hover:underline font-medium"
                              title="View candidate insights"
                            >
                              <Sparkles className="h-3.5 w-3.5" />
                              {a.candidate_name}
                            </button>
                          </td>
                          <td className="px-4 py-4">
                            {(() => {
                              const at = a.applicant_type || "unknown";
                              const label = at === "overseas_eligible" ? "Overseas Eligible" : at === "international" ? "International" : at === "local" ? "Local" : "Unknown";
                              const cls = at === "local" ? "bg-emerald-100 text-emerald-700 border-emerald-200" : at === "overseas_eligible" ? "bg-sky-100 text-sky-700 border-sky-200" : at === "international" ? "bg-violet-100 text-violet-700 border-violet-200" : "bg-amber-100 text-amber-700 border-amber-200";
                              const loc = a.country || a.current_location || "—";
                              return (
                                <div>
                                  <span data-testid={`app-type-${a.id}`} className={`inline-block text-[10px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded-full border ${cls}`}>{label}</span>
                                  <div className="text-[11px] text-slate-500 capitalize mt-0.5">{typeof loc === "string" ? loc : "—"}</div>
                                  <Select value={a.applicant_type || "unknown"} onValueChange={(v) => classifyOne(a.id, v)}>
                                    <SelectTrigger data-testid={`app-classify-${a.id}`} className="mt-1 h-7 w-32 text-[10px] bg-white"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="local">Mark Local</SelectItem>
                                      <SelectItem value="overseas_eligible">Mark Overseas Elig.</SelectItem>
                                      <SelectItem value="international">Mark International</SelectItem>
                                      <SelectItem value="unknown">Needs Review</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                              );
                            })()}
                          </td>
                          <td className="px-6 py-4 text-slate-600">{a.job_title}</td>
                          <td className="px-6 py-4 text-slate-600">{new Date(a.created_at).toLocaleDateString()}</td>
                          <td className="px-6 py-4 text-slate-600 text-xs">
                            <div>{a.candidate_email}</div>
                            <div>{a.candidate_phone}</div>
                          </td>
                          <td className="px-6 py-4">
                            <MatchScoreBadge score={a.match_score ?? null} />
                          </td>
                          <td className="px-6 py-4">
                            <QualificationBadge score={a.match_score ?? null} qualification={a.qualification} />
                          </td>
                          <td className="px-6 py-4">
                            <Select value={a.status} onValueChange={(v) => updateStatus(a.id, v)}>
                              <SelectTrigger data-testid={`status-select-${a.id}`} className="h-9 w-40 bg-white"><StatusBadge status={a.status} /></SelectTrigger>
                              <SelectContent>
                                {STATUS_LIST.map(s => <SelectItem key={s} value={s}>{t(`status.${s}`)}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="px-6 py-4">
                            <Button size="sm" variant="outline" data-testid={`view-cv-${a.id}`} onClick={() => viewCV(a.cv_file_id)} className="rounded-full">
                              <FileDown className="h-3 w-3 mr-1" />CV
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </TabsContent>

          <TabsContent value="jobs" className="mt-6">
            {jobs.length === 0 ? (
              <Card className="p-12 text-center text-slate-500">{t("employer.noJobs")}</Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {jobs.map(j => {
                  const qc = j.qualification_counts || { qualified: 0, needs_review: 0, not_qualified: 0, pending: 0 };
                  return (
                  <Card key={j.id} className="p-5" data-testid={`emp-job-${j.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h3 className="font-display font-semibold text-slate-900">{j.title}</h3>
                        <p className="text-sm text-slate-500 mt-1">{j.location} · {j.contract_type}</p>
                      </div>
                      <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${j.status === 'active' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>{j.status}</span>
                    </div>
                    <div className="mt-4 flex items-center gap-4 text-xs text-slate-500">
                      <span className="inline-flex items-center gap-1"><Eye className="h-3 w-3" />{j.views || 0}</span>
                      <span className="inline-flex items-center gap-1"><Users className="h-3 w-3" />{j.applications_count || 0}</span>
                      <span>{j.apply_clicks || 0} clicks</span>
                    </div>
                    {/* Qualification breakdown */}
                    <div className="mt-4 pt-3 border-t border-slate-100">
                      <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-2">Candidate quality</div>
                      <div className="grid grid-cols-3 gap-2" data-testid={`job-qual-stats-${j.id}`}>
                        <Stat label="Qualified" value={qc.qualified} color="text-emerald-600" bg="bg-emerald-50" testid={`job-qual-qualified-${j.id}`} />
                        <Stat label="Needs review" value={qc.needs_review} color="text-amber-600" bg="bg-amber-50" testid={`job-qual-needs_review-${j.id}`} />
                        <Stat label="Not qualified" value={qc.not_qualified} color="text-rose-600" bg="bg-rose-50" testid={`job-qual-not_qualified-${j.id}`} />
                      </div>
                      {qc.pending > 0 && (
                        <div className="mt-1.5 text-[10px] text-slate-400" data-testid={`job-qual-pending-${j.id}`}>
                          {qc.pending} {qc.pending === 1 ? "application" : "applications"} pending analysis
                        </div>
                      )}
                    </div>
                  </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          <TabsContent value="reports" className="mt-6">
            <EmployerReports />
          </TabsContent>

          <TabsContent value="candidates" className="mt-6">
            <CandidateDatabase />
          </TabsContent>

          <TabsContent value="career" className="mt-6">
            <EmployerCareerEditor />
          </TabsContent>
        </Tabs>
      </div>
      <ApplicationInsightsDialog
        applicationId={insightAppId}
        open={insightOpen}
        onOpenChange={(o) => { setInsightOpen(o); if (!o) setInsightAppId(null); }}
      />
    </div>
  );
}

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <Card className="p-5" data-testid={`stat-${label}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-widest text-slate-500 font-medium">{label}</span>
        <Icon className={`h-5 w-5 ${accent}`} />
      </div>
      <div className="font-display text-3xl font-bold text-slate-900 mt-2">{value}</div>
    </Card>
  );
}

function Stat({ label, value, color, bg, testid }) {
  return (
    <div data-testid={testid} className={`rounded-lg ${bg} px-2 py-2 text-center`}>
      <div className={`text-lg font-bold ${color}`}>{value || 0}</div>
      <div className="text-[10px] text-slate-600 leading-tight mt-0.5">{label}</div>
    </div>
  );
}
